import * as React from "react";
import {useCallback, useMemo} from "react";
import {useAppTheme} from "@/app/AppThemeContext";
import {formatCustomerRequestNumber} from "@/model/request";
import {DateFormatter} from "@/lib/components/DateFormate";
import {CartonGradeCatalogDTO, RequestFilter, RequestOverviewRowDTO, RequestSortField} from "@/api/data-contracts";
import {getI18n, useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {DataTable} from "@/lib/components/DataTable";
import {ColumnDef} from "@tanstack/react-table";
import {Box, Grid2, Tooltip} from "@mui/material";
import {CommonI18N, RequestI18N} from "@/translations";
import {requestStatusField} from "@/common/request-details/request-status-field";
import {stringField} from "@/common/fields/string-field";
import {dateRangeField} from "@/common/fields/date-range-field";
import {quoteStatusField} from "@/common/request-details/quote-status-field";
import {dimensionsField} from "@/common/request-details/internal-dimensions-field";
import {KILOMETRE_UNIT, TOOLTIP_DELAY_MS} from "@/constants";
import {HourglassEmptyOutlined, WarningAmberOutlined} from "@mui/icons-material";
import {useCartonGrades} from "@/datacaches";
import {isOperatorRole, useAuth} from "@/app/auth/AuthContext";
import {formatAddressToSingleLine} from "@/model/address";
import {RequestInfoCell} from "@/pages/request-overview-page/RequestInfoCell";
import {RequestStatusCell} from "@/pages/request-overview-page/RequestStatusCell";
import {RequestValidUntilCell} from "@/pages/request-overview-page/RequestValidUntilCell";
import {RequestDistanceHeader} from "@/pages/request-overview-page/RequestDistanceHeader";
import {UnreadTotalHeader} from "@/common/overview-table/UnreadTotalHeader";
import {RequestCartonGradeCell} from "@/pages/request-overview-page/RequestCartonGradeCell";
import {
    RequestPriceCell,
    RequestQuantityCell,
    RequestTotalPriceCell
} from "@/pages/request-overview-page/RequestQuantityOptionsCell";
import {Constants} from "@/api/constants";
import {UseRequestOverviewStateResult} from "@/common/overview-state/useRequestOverviewState";
import {SortDirection} from "@/common/overview-state/overview-state-model";
import {requestFilterFieldGroupsFor} from "@/common/overview-state/request-view-state";
import {activeFilterColumnIds, countDeviatingFilterGroups} from "@/common/overview-state/filter-fields";
import {OverviewFilterBar} from "@/common/overview-table/OverviewFilterBar";
import {OverviewPagination} from "@/common/overview-table/OverviewPagination";
import {renderRequestColumnFilter} from "@/pages/request-overview-page/RequestColumnFilters";
import {REQUEST_OVERVIEW_COLUMN_IDS} from "@/common/overview-table/overview-table-column-ids";

const renderInfoCell = ({row}: {row: {original: RequestOverviewRowDTO}}) => (
    <RequestInfoCell request={row.original}/>
);

const renderDistanceHeader = () => <RequestDistanceHeader/>;

function makeStatusCell(type: RequestFilter) {
    function StatusCell(row: Readonly<{row: {original: RequestOverviewRowDTO}}>) {
        return <RequestStatusCell row={row.row.original} type={type}/>;
    }
    return StatusCell;
}

function makeValidUntilCell(dateFormat: string) {
    function ValidUntilCell({row}: Readonly<{row: {original: RequestOverviewRowDTO}}>) {
        return <RequestValidUntilCell row={row.original} dateFormat={dateFormat}/>;
    }
    return ValidUntilCell;
}

function makeCartonGradeCell(grades: CartonGradeCatalogDTO[] | undefined, locale: string) {
    function CartonGradeCell({row}: Readonly<{row: {original: RequestOverviewRowDTO}}>) {
        return <RequestCartonGradeCell row={row.original} grades={grades} locale={locale}/>;
    }
    return CartonGradeCell;
}

function makeDistanceCell(t: (key: string) => string, locale: string) {
    function DistanceCell({row}: Readonly<{row: {original: RequestOverviewRowDTO}}>) {
        return <>{renderDistanceCell(row.original, t, locale)}</>;
    }
    return DistanceCell;
}

function makeQuantityCell(locale: string) {
    function QuantityCell(row: Readonly<{row: {original: RequestOverviewRowDTO}}>) {
        return <RequestQuantityCell quantityOptions={row.row.original.quantityOptions} locale={locale}/>;
    }
    return QuantityCell;
}

function makePriceCell(locale: string) {
    function PriceCell(row: Readonly<{row: {original: RequestOverviewRowDTO}}>) {
        return <RequestPriceCell quantityOptions={row.row.original.quantityOptions} locale={locale}/>;
    }
    return PriceCell;
}

function makeTotalPriceCell(locale: string) {
    function TotalPriceCell(row: Readonly<{row: {original: RequestOverviewRowDTO}}>) {
        return <RequestTotalPriceCell quantityOptions={row.row.original.quantityOptions} locale={locale}/>;
    }
    return TotalPriceCell;
}

export interface RequestOverviewTableProps {
    type: RequestFilter;
    target: (id: number) => string;
    /**
     * The full overview-state result (#1607) — replaces the previous `options`/`statuses`/
     * `onChangeStatuses`/`companyId`/`onChangeCompanyId`/`showCompanyFilter`/`data` props. The table reads
     * rows, paging and sort straight from it and wires the filter bar and pagination controls to its
     * mutators.
     */
    state: UseRequestOverviewStateResult;
    /**
     * When true the component fills its parent height and lets the DataTable
     * scroll internally so that stickyHeader works correctly. Default: false.
     */
    fillHeight?: boolean;
}

function renderDistanceCell(row: RequestOverviewRowDTO, t: (key: string) => string, locale: string): React.ReactNode {
    const status = row.deliveryDistanceStatus;
    const meters = row.distanceMeters;

    let tooltipTitle: React.ReactNode = "";
    let hasTooltip = false;

    if (row.hideDeliveryAddress) {
        tooltipTitle = t(RequestI18N.details.tooltip.deliveryAddressHidden);
        hasTooltip = true;
    } else if (row.deliveryAddress) {
        tooltipTitle = formatAddressToSingleLine(t, row.deliveryAddress, locale);
        hasTooltip = true;
    }

    let cellContent: React.ReactNode = null;
    if (status === "PENDING") {
        cellContent = <HourglassEmptyOutlined fontSize="small" color="disabled" />;
    } else if (status === "ERROR") {
        cellContent = <WarningAmberOutlined fontSize="small" color="warning" />;
    } else if (status === "CALCULATED" && meters !== null && meters !== undefined) {
        cellContent = (
            <>
                {Math.round(meters / 1000)} {KILOMETRE_UNIT}
            </>
        );
    }

    if (!hasTooltip) {
        return cellContent;
    }

    return (
        <Tooltip
            title={tooltipTitle}
            enterDelay={TOOLTIP_DELAY_MS}
            enterNextDelay={TOOLTIP_DELAY_MS}
            arrow
        >
            <span>{cellContent}</span>
        </Tooltip>
    );
}

/**
 * Pure UI component for rendering a request overview table with the shared filter bar, sortable column
 * headers with their own filter dropdowns and page navigation (#1607). Filter/sort/paging state is
 * controlled externally via `state` so it can be shared between embedded and fullscreen views.
 */
export const RequestOverviewTable: React.FC<RequestOverviewTableProps> = ({
    type,
    target,
    state,
    fillHeight = false
}) => {
    const { t } = useTranslation();
    const theme = useAppTheme();
    const locale = getI18n().language;
    const dateFormat = DateFormatter();
    const navigate = useNavigate();
    const [grades] = useCartonGrades();
    const { roles } = useAuth();
    const isOperator = isOperatorRole(roles);

    const handleRowClick = useCallback(
        (row: RequestOverviewRowDTO) => {
            if (row.id != null) {
                navigate(target(row.id));
            }
        },
        [navigate, target]
    );

    const columns = useMemo<ColumnDef<RequestOverviewRowDTO>[]>(
        () => [
            {
                id: REQUEST_OVERVIEW_COLUMN_IDS.info,
                header: () => (
                    <UnreadTotalHeader
                        label={t(RequestI18N.details.field.info)}
                        total={state.totalUnreadMessages}
                    />
                ),
                meta: {
                    disableTooltip: true,
                    filterColumnId: REQUEST_OVERVIEW_COLUMN_IDS.info
                },
                cell: renderInfoCell
            },
            {
                accessorKey: REQUEST_OVERVIEW_COLUMN_IDS.customerRequestNumber,
                header: t(RequestI18N.details.field.customerRequestNumber),
                meta: {
                    sortField: RequestSortField.CUSTOMER_REQUEST_NUMBER,
                    filterColumnId: REQUEST_OVERVIEW_COLUMN_IDS.customerRequestNumber
                },
                accessorFn: (row) =>
                    stringField(
                        formatCustomerRequestNumber(row) ?? undefined,
                            t(RequestI18N.details.field.customerRequestNumber),
                            t(CommonI18N.message.notGiven)
                    ).value
            },
            ...(type === RequestFilter.EXTERNAL || (type === RequestFilter.INTERNAL && isOperator)
                ? [
                      {
                          accessorKey: REQUEST_OVERVIEW_COLUMN_IDS.requesterCompany,
                          header: t(RequestI18N.details.field.requesterCompany),
                          meta: {
                              sortField: RequestSortField.REQUESTER_COMPANY,
                              filterColumnId: REQUEST_OVERVIEW_COLUMN_IDS.requesterCompany
                          },
                          accessorFn: (row: RequestOverviewRowDTO) =>
                              stringField(
                                  row.company?.name,
                                      t(RequestI18N.details.field.requesterCompany),
                                      t(CommonI18N.message.notGiven)
                              ).value
                      } as ColumnDef<RequestOverviewRowDTO>
                  ]
                : []),
            {
                accessorKey: REQUEST_OVERVIEW_COLUMN_IDS.status,
                header: t(RequestI18N.details.field.status),
                meta: {sortField: RequestSortField.STATUS, filterColumnId: REQUEST_OVERVIEW_COLUMN_IDS.status},
                accessorFn: (row) =>
                    type === RequestFilter.INTERNAL
                        ? requestStatusField(
                              {
                                  requestStatus: row.requestStatus,
                                  statusValue: row.requestStatus && t(CommonI18N.enum.requestStatus[row.requestStatus]),
                                  submittedQuoteCount: row.submittedQuoteCount
                              },
                                    t(RequestI18N.details.field.status),
                              theme,
                              {
                                  requestStatus: row.requestStatus,
                                  statusValue: row.requestStatus && t(CommonI18N.enum.requestStatus[row.requestStatus])
                              }
                          )
                        : quoteStatusField(
                              {
                                  quoteStatus: row.quoteStatus,
                                  statusValue: row.quoteStatus && t(CommonI18N.enum.quoteStatus[row.quoteStatus])
                              },
                                    t(RequestI18N.details.field.status),
                              theme,
                              {
                                  quoteStatus: row.quoteStatus,
                                  statusValue: row.quoteStatus && t(CommonI18N.enum.quoteStatus[row.quoteStatus])
                              }
                          ),
                cell: makeStatusCell(type)
            },
            {
                accessorKey: REQUEST_OVERVIEW_COLUMN_IDS.validUntil,
                header: t(RequestI18N.details.column.validUntil),
                meta: {
                    disableTooltip: true,
                    sortField: RequestSortField.VALID_UNTIL,
                    filterColumnId: REQUEST_OVERVIEW_COLUMN_IDS.validUntil
                },
                cell: makeValidUntilCell(dateFormat)
            },
            ...(type === RequestFilter.EXTERNAL
                ? [
                      {
                          accessorKey: REQUEST_OVERVIEW_COLUMN_IDS.distanceMeters,
                          header: renderDistanceHeader,
                          meta: {
                              disableTooltip: true,
                              sortField: RequestSortField.DISTANCE,
                              filterColumnId: REQUEST_OVERVIEW_COLUMN_IDS.distanceMeters
                          },
                          cell: makeDistanceCell(t, locale),
                          accessorFn: (row: RequestOverviewRowDTO) =>
                              row.distanceMeters !== null && row.distanceMeters !== undefined
                                  ? Math.round(row.distanceMeters / 1000)
                                  : null
                      } as ColumnDef<RequestOverviewRowDTO>
                  ]
                : []),
            {
                accessorKey: REQUEST_OVERVIEW_COLUMN_IDS.deliveryDateRange,
                header: t(RequestI18N.details.field.deliveryDate),
                meta: {
                    sortField: RequestSortField.DELIVERY_DATE,
                    filterColumnId: REQUEST_OVERVIEW_COLUMN_IDS.deliveryDateRange
                },
                accessorFn: (row) =>
                    dateRangeField(
                        row.deliveryDateFrom,
                        row.deliveryDateTo,
                            t(RequestI18N.details.field.deliveryDateRange),
                            t(CommonI18N.message.notGiven),
                        dateFormat
                    ).value
            },
            {
                accessorKey: REQUEST_OVERVIEW_COLUMN_IDS.cartonGrade,
                header: t(RequestI18N.details.field.variety),
                meta: {
                    disableTooltip: true,
                    sortField: RequestSortField.CARTON_GRADE,
                    filterColumnId: REQUEST_OVERVIEW_COLUMN_IDS.cartonGrade
                },
                cell: makeCartonGradeCell(grades, locale)
            },
            {
                accessorKey: REQUEST_OVERVIEW_COLUMN_IDS.fefcoCode,
                header: "FEFCO",
                meta: {sortField: RequestSortField.FEFCO_CODE, filterColumnId: REQUEST_OVERVIEW_COLUMN_IDS.fefcoCode},
                accessorFn: (row) =>
                        stringField(row.fefcoCode, t(RequestI18N.details.field.fefcoCode), t(CommonI18N.message.notGiven)).value
            },
            {
                accessorKey: REQUEST_OVERVIEW_COLUMN_IDS.internalDimensions,
                header: t(RequestI18N.details.field.internalDimensions),
                meta: {
                    sortField: RequestSortField.INTERNAL_DIMENSIONS,
                    filterColumnId: REQUEST_OVERVIEW_COLUMN_IDS.internalDimensions
                },
                accessorFn: (row) =>
                    dimensionsField(
                        row.internalDimension,
                            t(RequestI18N.details.field.internalDimensions),
                            t(CommonI18N.message.notGiven),
                        locale
                    ).value
            },
            {
                accessorKey: REQUEST_OVERVIEW_COLUMN_IDS.quantityOptions1,
                header: t(RequestI18N.list.column.quantity),
                meta: {
                    sortField: RequestSortField.QUANTITY,
                    filterColumnId: REQUEST_OVERVIEW_COLUMN_IDS.quantityOptions1
                },
                cell: makeQuantityCell(locale)
            },
            {
                accessorKey: REQUEST_OVERVIEW_COLUMN_IDS.quantityOptions2,
                header: t(RequestI18N.details.field.netPriceShort),
                meta: {
                    sortField: RequestSortField.NET_PRICE,
                    filterColumnId: REQUEST_OVERVIEW_COLUMN_IDS.quantityOptions2
                },
                cell: makePriceCell(locale)
            },
            {
                accessorKey: REQUEST_OVERVIEW_COLUMN_IDS.quantityOptions3,
                header: t(RequestI18N.details.field.totalPriceShort),
                meta: {
                    sortField: RequestSortField.TOTAL_PRICE,
                    filterColumnId: REQUEST_OVERVIEW_COLUMN_IDS.quantityOptions3
                },
                cell: makeTotalPriceCell(locale)
            }
        ],
        [t, type, theme, locale, dateFormat, grades, isOperator, state.totalUnreadMessages]
    );

    /**
     * `DataTable#onSortChange` is generic over `meta.sortField` (a plain `string`, #1607) — it has no
     * knowledge of `RequestSortField`. Every column here sets `meta.sortField` exclusively to
     * `RequestSortField` enum members (never a string literal), so the value this callback ever receives
     * from `DataTable` is always a genuine `RequestSortField`; the cast simply names that fact at the
     * boundary `state.setSort` enforces.
     */
    const handleSortChange = useCallback(
        (field: string | undefined, direction: SortDirection | undefined) =>
            state.setSort(field as RequestSortField | undefined, direction),
        [state]
    );

    // Memoized together, mirroring `OrderTrackingTable.tsx`'s fix for the same defect (#1607 review):
    // `activeFilterColumnIds` returns a new `Set` on every call, an inline (non-memoized) call would defeat
    // a future `React.memo` on `DataTable` on every keystroke.
    const {badgeCount, activeColumnIds} = useMemo(
        () => ({
            badgeCount: countDeviatingFilterGroups(state.applied.filters, state.defaults, requestFilterFieldGroupsFor(type)),
            activeColumnIds: activeFilterColumnIds(state.applied.filters, state.defaults, requestFilterFieldGroupsFor(type))
        }),
        [state.applied.filters, state.defaults, type]
    );

    // Filter content for the select/text columns wired in Phase 3.1 (status, Anfragenummer, Anfragesteller,
    // Sorte, FEFCO, Info) plus the range columns wired in Phase 3.2 (Innenmaße, Anfragemenge, Netto,
    // Gesamtpreis, Gültig bis, Lieferzeitraum, and, Angebotsgebersicht only, Entfernung) — all routed
    // through `renderRequestColumnFilter`'s single switch.
    const renderColumnFilter = useCallback(
        (columnId: string) => renderRequestColumnFilter(columnId, type, state.applied.filters, state.setFilters),
        [type, state.applied.filters, state.setFilters]
    );

    const filterBar = (
        <OverviewFilterBar
            searchText={state.applied.searchText}
            onSearchTextChange={state.setSearchText}
            searchTextMaxLength={Constants.REQUEST_SEARCH_TEXT_LENGTH_MAX}
            badgeCount={badgeCount}
            onReset={state.resetAll}
        />
    );

    const pagination = (
        <OverviewPagination
            page={state.page}
            rowsPerPage={state.size}
            totalElements={state.totalElements}
            totalPages={state.totalPages}
            rowsPerPageOptions={state.sizeOptions}
            onPageChange={state.setPage}
            onRowsPerPageChange={state.setSize}
        />
    );

    const table = (
        <DataTable
            columns={columns}
            data={state.rows}
            noResultsValue={t(RequestI18N.list.message.notFound)}
            striped
            maxHeight={fillHeight ? "100%" : undefined}
            onRowClick={handleRowClick}
            sortField={state.applied.sortField}
            sortDirection={state.applied.sortDirection}
            onSortChange={handleSortChange}
            renderColumnFilter={renderColumnFilter}
            activeFilterColumnIds={activeColumnIds}
        />
    );

    if (fillHeight) {
        return (
            <Box display="flex" flexDirection="column" height="100%" minHeight={0} gap={2} paddingTop={1}>
                {filterBar}
                <Box flex={1} minHeight={0} width="100%">
                    {table}
                </Box>
                {pagination}
            </Box>
        );
    }

    return (
        <Grid2 container direction="column" spacing={4} paddingTop={1}>
            <Grid2 sx={{position: "sticky", top: 0, zIndex: 1, bgcolor: "background.paper"}}>
                {filterBar}
            </Grid2>
            <Grid2 overflow="auto" width="100%">
                {table}
            </Grid2>
            <Grid2>
                {pagination}
            </Grid2>
        </Grid2>
    );
};
